import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  TextField,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PageLayout from "../../components/layout/PageLayout";
import Can from "../../components/permissions/Can";
import { useAuth } from "../../hooks/useAuth";
import {
  useGetServicesQuery,
  useDeleteServiceMutation,
  useApproveServiceMutation,
} from "../../store/api";
import { extractPagination } from "../../utils/response";
import { toast } from "react-toastify";
import { SweetAlert } from "../../utils/sweetAlert";
import { buildServiceRelationParams } from "./serviceApiHelpers";

const statusLabel = (row) =>
  row?.status?.name ?? row?.status?.label ?? row?.status_name ?? "—";

const authorLabel = (row) => row?.author?.name ?? row?.author?.username ?? "—";

const createdAtLabel = (row) => row?.created_at ?? "—";

const formatChargeUsd = (value) => {
  if (value === undefined || value === null || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return `$${value}`;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const parseIsApproved = (val) => {
  if (val === true || val === 1 || val === "1") return true;
  if (val === false || val === 0 || val === "0") return false;
  const s = String(val ?? "")
    .trim()
    .toLowerCase();
  if (["yes", "true", "approved"].includes(s)) return true;
  if (["no", "false", "unapproved", "pending"].includes(s)) return false;
  return null;
};

const isServiceApproved = (row) => parseIsApproved(row?.is_approved) === true;

const approvedCell = (row, { onApprove, approving, canApprove }) => {
  const approved = parseIsApproved(row?.is_approved);
  if (approved === null) return "—";
  if (approved) {
    return <Chip size="small" label="Yes" color="success" variant="filled" />;
  }
  if (!canApprove) {
    return <Chip size="small" label="No" color="warning" variant="outlined" />;
  }
  return (
    <Chip
      size="small"
      label={approving ? "Approving…" : "No"}
      color="warning"
      variant="outlined"
      disabled={approving}
      onClick={(e) => {
        e.stopPropagation();
        onApprove?.(row);
      }}
      sx={{ cursor: approving ? "default" : "pointer" }}
      title="Click to approve this service"
    />
  );
};

const ServicesList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canApproveService = hasPermission("service-is_approved");
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
  });
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const queryParams = useMemo(
    () => ({
      page: pagination.page,
      per_page: pagination.per_page,
      paginate: 1,
      ...buildServiceRelationParams(),
      ...(search ? { search } : {}),
    }),
    [pagination.page, pagination.per_page, search],
  );

  const { data, isFetching: loading, error } = useGetServicesQuery(queryParams);
  const [deleteService] = useDeleteServiceMutation();
  const [approveService] = useApproveServiceMutation();

  const rows = useMemo(() => {
    const list =
      data?.data?.list ??
      data?.list ??
      (Array.isArray(data?.data) ? data.data : []);
    return Array.isArray(list) ? list : [];
  }, [data]);

  useEffect(() => {
    if (!data) return;
    const pag = extractPagination(data);
    setPagination((p) => ({ ...p, total: pag.total ?? 0 }));
  }, [data]);

  useEffect(() => {
    if (error) toast.error(error?.data?.message || "Failed to load services");
  }, [error]);

  const handleChangePage = (_, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    const per = parseInt(event.target.value, 10) || 10;
    setPagination((prev) => ({ ...prev, page: 1, per_page: per }));
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSearch(searchInput.trim());
  };

  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const goEdit = () => {
    const id = selectedRow?.id;
    handleCloseMenu();
    if (id) navigate(`/services/edit/${id}`);
  };

  const goView = () => {
    const id = selectedRow?.id;
    handleCloseMenu();
    if (id) navigate(`/services/view/${id}`);
  };

  const handleApprove = async (row) => {
    if (!canApproveService) {
      toast.warn("You do not have permission to approve services");
      return;
    }
    const target = row ?? selectedRow;
    if (!target?.id) return;
    if (isServiceApproved(target)) {
      toast.info("Service is already approved");
      return;
    }

    const result = await SweetAlert.confirm({
      title: "Approve service?",
      text: `Approve "${target.name ?? "this service"}"?`,
      confirmButtonText: "Approve",
      icon: "question",
    });
    if (!result.isConfirmed) return;

    handleCloseMenu();
    setActionLoadingId(target.id);
    try {
      const fd = new FormData();
      fd.append("_method", "PUT");
      await approveService({ id: target.id, data: fd }).unwrap();
      toast.success("Service approved");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to approve service");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = async () => {
    const row = selectedRow;
    if (!row?.id) return;
    handleCloseMenu();
    const result = await SweetAlert.confirm({
      title: "Delete service?",
      text: "This action cannot be undone.",
      confirmButtonText: "Delete",
      icon: "warning",
    });
    if (!result.isConfirmed) return;

    setActionLoadingId(row.id);
    try {
      await deleteService(row.id).unwrap();
      toast.success("Service deleted");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to delete service");
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            variant="h4"
            sx={{ color: "primary.main", fontWeight: 600 }}
          >
            Services
          </Typography>
          <Can permission="service-create">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/services/create")}
            >
              Add service
            </Button>
          </Can>
        </Box>

        <Box
          sx={{
            mb: 3,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            placeholder="Search services..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
          />
          <Button variant="outlined" onClick={handleSearch}>
            Search
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setSearchInput("");
              setSearch("");
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
          >
            Clear
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ overflowX: "auto" }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "grey.100" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Service Category
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Internal charges
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      External charges
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created at</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Author</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Approved</TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, width: 72, textAlign: "center" }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No services found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() =>
                          row.id && navigate(`/services/view/${row.id}`)
                        }
                        sx={{ cursor: row.id ? "pointer" : "default" }}
                        aria-label="View service"
                      >
                        <TableCell>{row.name ?? "—"}</TableCell>
                        <TableCell>
                          {row.service_category.name ?? "—"}
                        </TableCell>
                        <TableCell>
                          {formatChargeUsd(row.internal_charges)}
                        </TableCell>
                        <TableCell>
                          {formatChargeUsd(row.external_charges)}
                        </TableCell>
                        <TableCell>{createdAtLabel(row)}</TableCell>
                        <TableCell>{authorLabel(row)}</TableCell>
                        <TableCell>{statusLabel(row)}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {approvedCell(row, {
                            onApprove: handleApprove,
                            approving: actionLoadingId === row.id,
                            canApprove: canApproveService,
                          })}
                        </TableCell>
                        <TableCell
                          onClick={(e) => e.stopPropagation()}
                          sx={{ textAlign: "center" }}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenMenu(e, row)}
                            disabled={actionLoadingId === row.id}
                            aria-label="Actions"
                          >
                            {actionLoadingId === row.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <MoreVertIcon />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && (
              <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={pagination.per_page}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page"
              />
            )}
          </CardContent>
        </Card>
      </Container>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Can permission="service-view">
          <MenuItem onClick={goView}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>
        </Can>
        <Can permission="service-edit">
          <MenuItem onClick={goEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        </Can>
        {selectedRow && !isServiceApproved(selectedRow) && (
          <Can permission="service-is_approved">
            <MenuItem
              onClick={() => handleApprove(selectedRow)}
              disabled={actionLoadingId === selectedRow?.id}
            >
              <ListItemIcon>
                <CheckCircleOutlineIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText>Approve</ListItemText>
            </MenuItem>
          </Can>
        )}
        <Can permission="service-delete">
          <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Can>
      </Menu>
    </PageLayout>
  );
};

export default ServicesList;
